import { useState, useEffect } from "react";

/**
 * SPLASH SCREEN - Apple iPhone Bubble Animation Style
 * Animasi logo dengan gaya bubble/breathing seperti iPhone boot animation
 */
export default function SplashAppleBubble({ onComplete }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    // Splash duration: 1.1 seconds total
    const timers = [
      setTimeout(() => setPhase(1), 100),    // Logo appears
      setTimeout(() => setPhase(2), 350),    // Text appears
      setTimeout(() => setPhase(3), 900),    // Fade out begins
      setTimeout(() => onComplete(), 1100)   // Complete - total 1.1s
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden transition-opacity duration-700 ${phase === 3 ? 'opacity-0' : 'opacity-100'}`}
      style={{ 
        background: 'linear-gradient(180deg, #FFF9E6 0%, #FFE5E5 30%, #E3F2FD 60%, #F3E5F5 100%)'
      }}
    >
      {/* Soft glow background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient blobs */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, #FF6B9D 0%, transparent 70%)',
            top: '-200px',
            left: '-100px',
            animation: 'blobFloat1 8s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute w-[500px] h-[500px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, #4DD0E1 0%, transparent 70%)',
            bottom: '-150px',
            right: '-100px',
            animation: 'blobFloat2 10s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute w-[400px] h-[400px] rounded-full opacity-25"
          style={{
            background: 'radial-gradient(circle, #FFB74D 0%, transparent 70%)',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'blobFloat3 6s ease-in-out infinite'
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center justify-center z-10">
        {/* Logo with Apple-style bubble animation */}
        <div 
          className={`relative transition-all duration-1000 ${
            phase >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
          }`}
        >
          {/* Outer glow ring - breathing */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,107,157,0.4) 0%, rgba(77,208,225,0.2) 50%, transparent 70%)',
              transform: 'scale(2.5)',
              animation: phase >= 1 ? 'bubbleBreath 2s ease-in-out infinite' : 'none'
            }}
          />
          
          {/* Middle glow ring */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,183,77,0.3) 0%, transparent 60%)',
              transform: 'scale(2)',
              animation: phase >= 1 ? 'bubbleBreath 2s ease-in-out infinite 0.3s' : 'none'
            }}
          />
          
          {/* Inner glow */}
          <div 
            className="absolute inset-0 rounded-full blur-xl"
            style={{
              background: 'linear-gradient(135deg, #FF6B9D, #FFB74D, #4DD0E1)',
              opacity: 0.5,
              animation: phase >= 1 ? 'bubbleGlow 2s ease-in-out infinite' : 'none'
            }}
          />
          
          {/* Logo container with bubble effect */}
          <div 
            className="relative"
            style={{
              animation: phase >= 1 ? 'appleBubble 2s ease-in-out infinite' : 'none'
            }}
          >
            <img 
              src="/logo-destiny.png" 
              alt="Destiny Junior" 
              className="w-40 h-40 md:w-52 md:h-52 object-contain drop-shadow-2xl relative z-10"
              style={{
                filter: 'drop-shadow(0 10px 30px rgba(255,107,157,0.4))'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* Title with staggered animation */}
        <div 
          className={`mt-8 transition-all duration-700 ${
            phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h1 
            className="text-4xl md:text-5xl font-black text-center"
            style={{ fontFamily: "'Fredoka', sans-serif" }}
          >
            <span 
              className="inline-block"
              style={{ 
                color: '#FF6B9D', 
                textShadow: '2px 2px 0 #FFE5EC',
                animation: phase >= 2 ? 'textPop 0.5s ease-out forwards' : 'none'
              }}
            >
              DESTINY
            </span>
            {' '}
            <span 
              className="inline-block"
              style={{ 
                color: '#4DD0E1', 
                textShadow: '2px 2px 0 #E0F7FA',
                animation: phase >= 2 ? 'textPop 0.5s ease-out 0.2s forwards' : 'none',
                opacity: 0
              }}
            >
              JUNIOR
            </span>
          </h1>
          
          <p 
            className="text-center text-gray-600 mt-3 text-base md:text-lg font-semibold"
            style={{
              animation: phase >= 2 ? 'fadeInUp 0.5s ease-out 0.4s forwards' : 'none',
              opacity: 0
            }}
          >
            Apps Care
          </p>
        </div>

        {/* Loading indicator - Apple style dots */}
        <div 
          className={`mt-10 flex gap-2 transition-opacity duration-500 ${
            phase >= 1 ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {[0, 1, 2].map((i) => (
            <div 
              key={i}
              className="w-2.5 h-2.5 rounded-full"
              style={{
                background: 'linear-gradient(135deg, #FF6B9D, #4DD0E1)',
                animation: 'dotPulse 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.16}s`
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes appleBubble {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.08);
          }
        }

        @keyframes bubbleBreath {
          0%, 100% {
            transform: scale(2.5);
            opacity: 0.3;
          }
          50% {
            transform: scale(2.8);
            opacity: 0.5;
          }
        }

        @keyframes bubbleGlow {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.1);
          }
        }

        @keyframes blobFloat1 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95);
          }
        }

        @keyframes blobFloat2 {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-40px, -30px) scale(1.15);
          }
        }

        @keyframes blobFloat3 {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.25;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.35;
          }
        }

        @keyframes textPop {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(10px);
          }
          60% {
            transform: scale(1.05) translateY(-2px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes dotPulse {
          0%, 80%, 100% {
            transform: scale(0.6);
            opacity: 0.4;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
